import { LoginPage as DesignSystemLoginPage } from "@arcadeai/design-system/components/ui/pages/login-page";
import {
  getSafeReturnTo,
  REGISTRATION_PROMPT,
  RETURN_TO_PARAM,
} from "@arcadeai/ui-kit/utils/auth-redirect";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { fallback, zodValidator } from "@tanstack/zod-adapter";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { RootLoader } from "@/components/custom/root-loader";
import { authClient, sessionQueryOptions } from "@/lib/auth-client";
import { ACCOUNT_UI_URL } from "@/lib/const";
import { queryClient } from "@/lib/query-client";

// `auto_login` is set by identity-ui to start the OIDC handshake immediately
// instead of stranding the user on this page — silently when an identity
// session exists, or on the provider screen selected by `prompt`
// (registration keeps account creation inside the OAuth2 challenge, so the
// new account is signed in when the flow completes).
const searchBooleanSchema = z.union([
  z.boolean(),
  z.enum(["true", "false"]).transform((value) => value === "true"),
]);

const searchParamsSchema = z.object({
  auto_login: fallback(searchBooleanSchema, false).default(false),
  return_to: fallback(z.string().optional(), undefined).default(undefined),
  prompt: fallback(z.enum([REGISTRATION_PROMPT]).optional(), undefined).default(
    undefined
  ),
});

function manualLoginSearch({
  prompt,
  returnTo,
}: {
  prompt?: typeof REGISTRATION_PROMPT;
  returnTo: string;
}) {
  return {
    return_to: returnTo,
    prompt,
  };
}

export const Route = createFileRoute("/login")({
  validateSearch: zodValidator(searchParamsSchema),
  pendingComponent: RootLoader,
  beforeLoad: async ({ search }) => {
    const session = await queryClient.fetchQuery({
      ...sessionQueryOptions,
      staleTime: 0,
    });
    const returnTo = getSafeReturnTo(search.return_to, window.location.origin);
    if (session) {
      throw redirect({ href: returnTo });
    }
    if (search.auto_login) {
      let authorizeUrl: string | undefined;
      try {
        const { data } = await authClient.signIn.oauth2({
          providerId: "oidc",
          callbackURL: returnTo,
          disableRedirect: true,
          additionalData: search.prompt ? { prompt: search.prompt } : undefined,
        });
        authorizeUrl = data?.url ?? undefined;
      } catch {
        // Fall through to the manual sign-in button.
      }
      if (authorizeUrl) {
        throw redirect({ href: authorizeUrl });
      }
      throw redirect({
        to: "/login",
        search: manualLoginSearch({ prompt: search.prompt, returnTo }),
      });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const { return_to: returnTo, prompt } = Route.useSearch();
  const [isLoading, setIsLoading] = useState(false);

  const registerUrl = new URL("/registerOrRedirect", ACCOUNT_UI_URL);
  if (returnTo) {
    registerUrl.searchParams.set(
      RETURN_TO_PARAM,
      getSafeReturnTo(returnTo, window.location.origin)
    );
  }

  const handleSubmit = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const { error } = await authClient.signIn.oauth2({
        providerId: "oidc",
        callbackURL: getSafeReturnTo(returnTo, window.location.origin),
        additionalData: prompt ? { prompt } : undefined,
      });
      if (error) {
        toast.error("Sign-in failed. Please try again.");
        setIsLoading(false);
      }
    } catch {
      toast.error("Sign-in failed. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <DesignSystemLoginPage
      isLoading={isLoading}
      onSubmit={handleSubmit}
      registerHref={registerUrl.toString()}
    />
  );
}
